(() => {
  const componentName = 'yellowcart-collection-navigation';
  if (customElements.get(componentName)) return;

  class YellowcartCollectionNavigation extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-yc-collection-navigation-list]');
      this.controls = this.closest('.yc-ref-collection-navigation')?.querySelector('[data-yc-navigation-controls]');
      this.previousButton = this.controls?.querySelector('[data-yc-navigation-previous]');
      this.nextButton = this.controls?.querySelector('[data-yc-navigation-next]');
      this.desktopSlider = this.dataset.desktopSlider === 'true';
      this.desktopMedia = window.matchMedia('(min-width: 990px)');
      this.scrollFrame = null;
      this.resizeTimer = null;

      if (!this.list || !this.controls || !this.previousButton || !this.nextButton || !this.desktopSlider) {
        if (this.controls) this.controls.hidden = true;
        return;
      }

      this.handlePrevious = () => this.move(-1);
      this.handleNext = () => this.move(1);
      this.handleScroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateControls();
        });
      };
      this.handleMediaChange = () => this.evaluate();
      this.handleResize = () => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.evaluate(), 80);
      };
      this.handleFocusIn = (event) => {
        const item = event.target.closest('.yc-collections__item');
        if (!item || !this.desktopMedia.matches) return;
        item.scrollIntoView({
          behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      };

      this.previousButton.addEventListener('click', this.handlePrevious);
      this.nextButton.addEventListener('click', this.handleNext);
      this.list.addEventListener('scroll', this.handleScroll, { passive: true });
      this.list.addEventListener('focusin', this.handleFocusIn);
      window.addEventListener('resize', this.handleResize, { passive: true });

      if (this.desktopMedia.addEventListener) {
        this.desktopMedia.addEventListener('change', this.handleMediaChange);
      } else {
        this.desktopMedia.addListener(this.handleMediaChange);
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.evaluate());
        this.resizeObserver.observe(this.list);
      }

      requestAnimationFrame(() => this.evaluate());
      if (document.fonts?.ready) document.fonts.ready.then(() => this.evaluate());
    }

    disconnectedCallback() {
      this.previousButton?.removeEventListener('click', this.handlePrevious);
      this.nextButton?.removeEventListener('click', this.handleNext);
      this.list?.removeEventListener('scroll', this.handleScroll);
      this.list?.removeEventListener('focusin', this.handleFocusIn);
      window.removeEventListener('resize', this.handleResize);

      if (this.desktopMedia) {
        if (this.desktopMedia.removeEventListener) {
          this.desktopMedia.removeEventListener('change', this.handleMediaChange);
        } else {
          this.desktopMedia.removeListener(this.handleMediaChange);
        }
      }

      this.resizeObserver?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      window.clearTimeout(this.resizeTimer);
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    getItems() {
      return Array.from(this.list?.querySelectorAll(':scope > .yc-collections__item') || []);
    }

    hasOverflow() {
      return Boolean(this.list && this.list.scrollWidth - this.list.clientWidth > 2);
    }

    evaluate() {
      const enabled = this.desktopSlider && this.desktopMedia.matches && this.hasOverflow();
      this.controls.hidden = !enabled;
      this.classList.toggle('is-enhanced', enabled);
      if (enabled) this.updateControls();
    }

    getCurrentIndex() {
      const items = this.getItems();
      if (!items.length) return 0;

      const listRect = this.list.getBoundingClientRect();
      const isRtl = getComputedStyle(this.list).direction === 'rtl';
      const listStart = isRtl ? listRect.right : listRect.left;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      items.forEach((item, index) => {
        const itemRect = item.getBoundingClientRect();
        const itemStart = isRtl ? itemRect.right : itemRect.left;
        const distance = Math.abs(itemStart - listStart);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    move(direction) {
      const items = this.getItems();
      if (!items.length) return;

      const targetIndex = Math.max(0, Math.min(items.length - 1, this.getCurrentIndex() + direction));
      items[targetIndex].scrollIntoView({
        behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    }

    updateControls() {
      if (this.controls.hidden) return;

      const items = this.getItems();
      if (!items.length) {
        this.controls.hidden = true;
        return;
      }

      const listRect = this.list.getBoundingClientRect();
      const firstRect = items[0].getBoundingClientRect();
      const lastRect = items[items.length - 1].getBoundingClientRect();
      const isRtl = getComputedStyle(this.list).direction === 'rtl';
      const tolerance = 2;

      const atStart = isRtl
        ? firstRect.right <= listRect.right + tolerance
        : firstRect.left >= listRect.left - tolerance;
      const atEnd = isRtl
        ? lastRect.left >= listRect.left - tolerance
        : lastRect.right <= listRect.right + tolerance;

      this.previousButton.disabled = atStart;
      this.nextButton.disabled = atEnd;
      this.previousButton.setAttribute('aria-disabled', String(atStart));
      this.nextButton.setAttribute('aria-disabled', String(atEnd));
    }
  }

  customElements.define(componentName, YellowcartCollectionNavigation);
})();
